import React, { ReactNode, useEffect, useState } from "react";
import AscendingIcon from "../../../Assets/Atoms/Table/SortAscending.svg";
import DescendingIcon from "../../../Assets/Atoms/Table/SortDescending.svg";
import LoadingPage from "../../../Pages/LoadingPage";

export interface DataSourceItem<T> {
  key: string;
  [key: string]: any;
  data: T & { [key: string]: any };
}

export interface Column<T> {
  title: string | ReactNode;
  dataIndex: string;
  key?: string;
  localSort?: (a: DataSourceItem<T>, b: DataSourceItem<T>) => number;
  sortClicked?: (order: "asc" | "desc" | "none") => void;
  width?: string;
  render?: (item: DataSourceItem<T>) => ReactNode;
  defaultSortOrder?: "asc" | "desc" | "none";
  align?: "left" | "center" | "right";
}

export type RowSelectionActionOn = "all" | "selected";

export interface RowSelectionOptions<T> {
  enableSelection: boolean;
  onRowSelectionChange?: (selectedRowKeys: string[], selectedRows: DataSourceItem<T>[]) => void;
  selected?: string[];
  selectionEnabled?: (d: DataSourceItem<T>) => boolean; // New function to determine if a row is selectable
}

interface Props<T> {
  dataSource: DataSourceItem<T>[];
  columns: Column<T>[];
  isLoading?: boolean;
  rowSelectionOptions?: RowSelectionOptions<T>;
}

const Table = <T,>({
  dataSource,
  columns,
  isLoading,
  rowSelectionOptions = {
    enableSelection: false
  }
}: Props<T>) => {
  const { enableSelection, onRowSelectionChange, selected, selectionEnabled } = rowSelectionOptions;

  // Default function for selectionEnabled if not provided
  const isRowSelectable = (row: DataSourceItem<T>) =>
    selectionEnabled ? selectionEnabled(row) : true;

  const [sortedInfo, setSortedInfo] = useState<{
    columnKey: string;
    order: "asc" | "desc" | "none";
  } | null>({ columnKey: "", order: "none" });

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<DataSourceItem<T>[]>([]);

  const handleRowSelectionChange = (row: DataSourceItem<T>) => {
    if (!isRowSelectable(row)) return; // Prevent selection if not allowed
    setSelectedRows(prevRows => {
      if (prevRows.find(r => r.key === row.key)) {
        return prevRows.filter(r => r.key !== row.key);
      } else {
        return [...prevRows, row];
      }
    });
    setSelectedRowKeys(prevKeys => {
      if (prevKeys.includes(row.key)) {
        return prevKeys.filter(k => k !== row.key);
      } else {
        return [...prevKeys, row.key];
      }
    });
  };

  const headerCheckboxRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        selectedRowKeys.length > 0 && selectedRowKeys.length < dataSource.length;
    }
  }, [selectedRowKeys, dataSource.length]);

  useEffect(() => {
    if (selected && selected.length !== selectedRowKeys.length) {
      // set selected rows based on selected prop
      const selectedRows = dataSource.filter(r => selected.includes(r.key));
      setSelectedRows(selectedRows);
      setSelectedRowKeys(selected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const selectableRows = dataSource.filter(isRowSelectable);
      setSelectedRows(selectableRows);
      setSelectedRowKeys(selectableRows.map(item => item.key));
    } else {
      setSelectedRows([]);
      setSelectedRowKeys([]);
    }
  };

  const selectionColumn: Column<T> = {
    title: (
      <input
        type="checkbox"
        onChange={handleSelectAllChange}
        checked={
          selectedRowKeys.length === dataSource.filter(isRowSelectable).length &&
          dataSource.filter(isRowSelectable).length !== 0
        }
        ref={headerCheckboxRef}
      />
    ),
    dataIndex: "selection",
    key: "selection",
    render: (item: DataSourceItem<T>) =>
      isRowSelectable(item) ? (
        <input
          type="checkbox"
          checked={!!selectedRows.find(r => r.key === item.key)}
          onChange={() => handleRowSelectionChange(item)}
        />
      ) : (
        <input type="checkbox" disabled />
      ), // Disable checkbox if row is not selectable
    width: "w-12"
  };

  const allColumns = enableSelection ? [selectionColumn, ...columns] : columns;

  const handleSort = (columnKey: string) => {
    let nextOrder: "asc" | "desc" | "none";
    if (
      sortedInfo?.columnKey !== columnKey ||
      !sortOrders[columnKey] ||
      sortOrders[columnKey] === "none"
    ) {
      nextOrder = "asc";
    } else if (sortOrders[columnKey] === "asc") {
      nextOrder = "desc";
    } else {
      nextOrder = "none";
    }
    setSortOrders({
      [columnKey]: nextOrder
    });
    setSortedInfo({ columnKey, order: nextOrder });

    const column = columns.find(col => col.dataIndex === columnKey);
    if (column?.sortClicked) {
      column.sortClicked(nextOrder);
    }
  };

  const sortedColumns = allColumns.map(column => ({
    ...column,
    onHeaderCell:
      column.localSort || column.sortClicked
        ? { onClick: () => !isLoading && handleSort(column.dataIndex) }
        : undefined
  }));

  const initialSortOrders = columns.reduce(
    (acc, column) => {
      if (column.defaultSortOrder) {
        acc[column.dataIndex] = column.defaultSortOrder;
      }
      return acc;
    },
    {} as Record<string, "asc" | "desc" | "none">
  );

  const [sortOrders, setSortOrders] =
    useState<Record<string, "asc" | "desc" | "none">>(initialSortOrders);

  const sortedData = [...dataSource].sort((a, b) => {
    const column = columns.find(col => col.dataIndex === sortedInfo?.columnKey);
    return column?.localSort && sortedInfo?.order !== "none"
      ? column.localSort(a, b) * (sortedInfo?.order === "asc" ? 1 : -1)
      : 0;
  });

  useEffect(() => {
    if (onRowSelectionChange) {
      onRowSelectionChange(selectedRowKeys, selectedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRowKeys, selectedRows]);

  return (
    <>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="sticky top-0 shadow-sm bg-white z-10">
          <tr>
            {sortedColumns.map(column => (
              <th
                key={column.dataIndex}
                scope="col"
                className={`h-12 py-3 text-bold ${
                  column.width ? column.width : ""
                } text-${column.align || "left"} ${
                  column.localSort || column.sortClicked
                    ? isLoading
                      ? "cursor-progress"
                      : "cursor-pointer hover:bg-gray-100"
                    : ""
                }`}
                {...column.onHeaderCell}
              >
                <span className="flex break-words px-2">
                  {column.title}
                  {(column.localSort || column.sortClicked) &&
                    ["asc", "desc"].includes(sortOrders[column.dataIndex] || "") && (
                      <img
                        className="mx-3"
                        src={
                          sortOrders[column.dataIndex] === "asc"
                            ? AscendingIcon
                            : sortOrders[column.dataIndex] === "desc"
                              ? DescendingIcon
                              : ""
                        }
                        alt="sort icon"
                      />
                    )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        {!isLoading && (
          <tbody className="divide-y divide-gray-200">
            {sortedData.map(data => (
              <tr key={data.key} className="h-12">
                {sortedColumns.map(column => (
                  <td
                    key={column.dataIndex}
                    className={`whitespace-nowrap px-2 text-${column.align || "left"}`}
                  >
                    {column.render
                      ? column.render(data)
                      : data[column.dataIndex] === undefined
                        ? data.data[column.dataIndex]
                        : data[column.dataIndex]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {isLoading && <LoadingPage />}
    </>
  );
};
export default Table;
